import React, { useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, CheckCircle } from "lucide-react";
import { toast } from "sonner";

interface DocumentsSectionProps {
  uploadedFiles: {
    kraPinDocument?: File;
    idCopyDocument?: File;
    passportPhoto?: File;
  };
  onFileUpload: (field: 'kraPinDocument' | 'idCopyDocument' | 'passportPhoto', file: File) => void;
}

export const MembershipDocumentsSection = ({ uploadedFiles, onFileUpload }: DocumentsSectionProps) => {
  const kraFileRef = useRef<HTMLInputElement>(null);
  const idFileRef = useRef<HTMLInputElement>(null);
  const photoFileRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (
    field: 'kraPinDocument' | 'idCopyDocument' | 'passportPhoto',
    event: React.ChangeEvent<HTMLInputElement>,
    allowedTypes: string[]
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    const isValidType = allowedTypes.some(type => {
      if (type === 'application/pdf') return file.type === 'application/pdf';
      if (type === 'image/*') return file.type.startsWith('image/');
      return false;
    });

    if (!isValidType) {
      toast.error(`Please select a valid file type: ${allowedTypes.join(', ')}`);
      return;
    }

    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    onFileUpload(field, file);
  };

  const FileUploadButton = ({ 
    field, 
    label, 
    allowedTypes, 
    fileRef, 
    required = false 
  }: {
    field: 'kraPinDocument' | 'idCopyDocument' | 'passportPhoto';
    label: string;
    allowedTypes: string[];
    fileRef: React.RefObject<HTMLInputElement>;
    required?: boolean;
  }) => {
    const isUploaded = !!uploadedFiles[field];
    
    return (
      <div className="border rounded-lg p-6 space-y-4 file-upload">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold flex items-center gap-2">
              {label}
              {required && <span className="text-destructive">*</span>}
            </h4>
            <p className="text-sm text-muted-foreground">
              Allowed formats: {allowedTypes.map(type => 
                type === 'application/pdf' ? 'PDF' : 
                type === 'image/*' ? 'Images' : type
              ).join(', ')} (Max 10MB)
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {isUploaded && (
              <CheckCircle className="h-5 w-5 text-green-500" />
            )}
            <Button
              type="button"
              variant={isUploaded ? "outline" : "default"}
              onClick={() => fileRef.current?.click()}
              className="hover-scale"
            >
              <Upload className="h-4 w-4 mr-2" />
              {isUploaded ? 'Replace' : 'Upload'}
            </Button>
          </div>
        </div>
        
        {isUploaded && (
          <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
            <FileText className="h-4 w-4" />
            <span>{uploadedFiles[field]?.name}</span>
          </div>
        )}
        
        <input
          ref={fileRef}
          type="file"
          accept={allowedTypes.join(',')}
          onChange={(e) => handleFileSelect(field, e, allowedTypes)}
          className="hidden"
        />
      </div>
    );
  };

  return (
    <Card className="form-section-card animate-fade-in hover-scale">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Required Attachments
        </CardTitle>
        <CardDescription>
          Please upload clear copies of the following documents. All documents are required for processing your application.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <FileUploadButton
          field="kraPinDocument"
          label="KRA PIN Certificate"
          allowedTypes={["application/pdf", "image/*"]}
          fileRef={kraFileRef}
          required
        />
        
        <FileUploadButton
          field="idCopyDocument"
          label="ID or Passport Copy"
          allowedTypes={["application/pdf", "image/*"]}
          fileRef={idFileRef}
          required
        />
        
        <FileUploadButton
          field="passportPhoto"
          label="Passport Photo (with name and signature)"
          allowedTypes={["image/*"]}
          fileRef={photoFileRef}
          required
        />

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">Document Requirements:</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• All documents must be clear and legible</li>
            <li>• Maximum file size: 10MB per document</li>
            <li>• Accepted formats: PDF, JPG, PNG</li>
            <li>• Passport photo should include your name and signature</li>
            <li>• Ensure all text in documents is readable</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};